// FormLogicRuleCard — one conditional-logic rule in the drawer: a collapsible
// card ("Rule N" chip · trash) holding the answer picker ("IF THE ANSWER IS" —
// a dropdown whose trigger and rows render the option as its tinted chip) and,
// once an answer is picked, the "THEN" action tiles (Ask questions · Require
// picture · Require video · Go to) with the picked action's sub-panel: Ask →
// the ANSWER TYPE select alone (the follow-up is born untitled and gets its
// question text via the properties panel), Go to → a select of the OTHER pages
// (a page already targeted by this field, or targeting this page back, is
// excluded — the legacy two-way guard).
//
// A checkList follow-up at depth 1 additionally expands inline: "ITS CHOICES"
// (the shared ChoiceOptionsEditor over the child's optionList) and
// "CONDITIONAL LOGIC ON THIS QUESTION" hosting depth-2 rule cards — this
// component recursing exactly once. Depth-2 checkList follow-ups get choices
// only; two rule levels is the cap (product decision, shallower than legacy's
// three). Handlers are fieldId-scoped rather than pre-bound so the nested
// cards can address the CHILD's rules with the same functions.
//
// The THEN block is deliberately gated on the answer (legacy gated the same
// way): an ask action always belongs to a complete rule, so a follow-up row
// can never sit orphaned behind an unanswered rule.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton, SelectDropdown } from '@/components/common';
import type { BuilderField, BuilderPage, BuilderRule } from './utils/builderModel';
import type { ChoiceOption, ElementProperties, LibraryElementType } from './utils/formElementDefaults';
import { chipStyle } from './utils/builderColors';
import { FIELD_LIBRARY } from './utils/fieldLibrary';
import ChoiceOptionsEditor from './ChoiceOptionsEditor';
import {
  deriveGotoMap,
  hasFreeRuleValue,
  takenRuleValues,
  type RuleActionSpec,
} from './utils/triggerOps';

/** The follow-up ("Ask questions") answer types — the legacy allowlist. */
const ANSWER_TYPES: LibraryElementType[] = [
  'text',
  'number',
  'checkList',
  'image',
  'location',
  'dateTime',
  'signature',
  'ratingSlider',
  'numberSlider',
];

/** Library entry by element type, built once at module load. The answer-type
 *  options need each entry's label AND icon, and a `find` per row would rescan
 *  FIELD_LIBRARY on every render. */
const LIBRARY_BY_TYPE = new Map(FIELD_LIBRARY.map((entry) => [entry.type, entry]));

/** THEN tiles in design order. */
const ACTION_TILES = [
  { kind: 'ask_a_question', icon: 'message-square-plus', labelKey: 'forms_builder_logic_ask' },
  { kind: 'require_picture', icon: 'camera', labelKey: 'forms_builder_logic_require_picture' },
  { kind: 'require_video', icon: 'video', labelKey: 'forms_builder_logic_require_video' },
  { kind: 'require_note', icon: 'sticky-note', labelKey: 'forms_builder_logic_require_note' },
  { kind: 'user_page', icon: 'corner-down-right', labelKey: 'forms_builder_logic_goto' },
] as const;

interface FormLogicRuleCardProps {
  /** The rule's owner (the canvas checkList at depth 1, a follow-up at 2). */
  field: BuilderField;
  rule: BuilderRule;
  /** 1-based position among the owner's rules — the "Rule N" chip. */
  index: number;
  /** 1 = rule on the canvas field, 2 = rule on a level-1 follow-up. */
  depth: 1 | 2;
  /** The active page's fields — resolves the rule's follow-up element. */
  fields: BuilderField[];
  pages: BuilderPage[];
  activePageId: string;
  /** Collapse state lives in the drawer — rule ids are unique across levels. */
  collapsedIds: Set<string>;
  onToggleCollapse: (ruleId: string) => void;
  /** fieldId-scoped handlers, shared with the nested level-2 cards. */
  onAddRule: (fieldId: string) => void;
  onSetRuleAnswer: (fieldId: string, ruleId: string, value: string) => void;
  onSetRuleAction: (fieldId: string, ruleId: string, spec: RuleActionSpec) => void;
  /** Ask for deletion — the drawer confirms (cascade) before committing. */
  onRequestDelete: (fieldId: string, ruleId: string) => void;
  /** Live property writes for a follow-up's choices (cascades run upstream). */
  onUpdateFieldProperties: (fieldId: string, properties: ElementProperties) => void;
  onApplyResponseSet: (fieldId: string, options: ChoiceOption[]) => void;
}

const optionsOf = (field: BuilderField): ChoiceOption[] =>
  ((field.properties.basicProperties as Record<string, unknown>).optionList as ChoiceOption[]) ??
  [];

const FormLogicRuleCard = ({
  field,
  rule,
  index,
  depth,
  fields,
  pages,
  activePageId,
  collapsedIds,
  onToggleCollapse,
  onAddRule,
  onSetRuleAnswer,
  onSetRuleAction,
  onRequestDelete,
  onUpdateFieldProperties,
  onApplyResponseSet,
}: FormLogicRuleCardProps) => {
  const { t } = useTranslation();
  const [answerOpen, setAnswerOpen] = useState(false);
  const collapsed = collapsedIds.has(rule.id);

  const options = optionsOf(field);
  // The picked option — matchingValue keys on the option VALUE (options
  // sharing a value conflate; legacy-exact). Show the first match's chip.
  const picked = rule.matchingValue
    ? options.find((o) => o.value === rule.matchingValue)
    : undefined;
  // An answer may drive at most ONE rule — the picker hides the values the
  // field's OTHER rules hold (options sharing a value hide together).
  const takenValues = takenRuleValues(field, rule.id);
  const pickableOptions = options.filter((o) => !takenValues.has(o.value));

  const action = rule.action;
  const child =
    action?.kind === 'ask_a_question'
      ? (fields.find((f) => f.id === action.childId) ?? null)
      : null;

  // Go-to targets: every OTHER page, minus this field's other go-to targets
  // and pages that jump back to this one (two-way guard); the rule's current
  // target always stays listed so the select never loses its value.
  const currentTarget = action?.kind === 'user_page' ? action.pageId : null;
  const gotoMap = deriveGotoMap(pages);
  const takenByField = new Set(
    field.rules.flatMap((r) =>
      r.id !== rule.id && r.action?.kind === 'user_page' ? [r.action.pageId] : [],
    ),
  );
  const pageOptions = pages
    .filter(
      (page) =>
        page.id === currentTarget ||
        (page.id !== activePageId &&
          !takenByField.has(page.id) &&
          !gotoMap.get(page.id)?.has(activePageId)),
    )
    .map((page) => ({
      value: page.id,
      label:
        (page.properties.basicProperties.pageTitle as string) || t('forms_builder_untitled_page'),
    }));

  const answerType = child?.elementType ?? 'text';

  // The child's choices editor writes through the same intercepted funnel the
  // properties panel uses, so option-edit cascades cover level-2 rules too.
  const setChildOptions = (target: BuilderField, optionList: ChoiceOption[]) =>
    onUpdateFieldProperties(target.id, {
      ...target.properties,
      basicProperties: {
        ...(target.properties.basicProperties as object),
        optionList,
      },
    } as ElementProperties);

  return (
    <section
      className={['form-logic__rule', depth === 2 && 'form-logic__rule--nested']
        .filter(Boolean)
        .join(' ')}
    >
      <div className='form-logic__rule-head'>
        <IconButton
          icon={collapsed ? 'chevron-down' : 'chevron-up'}
          variant='outline'
          size='sm'
          aria-label={t(collapsed ? 'forms_builder_field_expand' : 'forms_builder_field_collapse')}
          aria-expanded={!collapsed}
          onClick={() => onToggleCollapse(rule.id)}
        />
        <span className='form-logic__rule-chip'>
          <Icon name='git-branch' size={11} />
          {depth === 2
            ? t('forms_builder_logic_level_rule', { n: index })
            : t('forms_builder_logic_rule_n', { n: index })}
        </span>
        <IconButton
          icon='trash-2'
          variant='ghost'
          size='sm'
          className='form-logic__rule-delete'
          aria-label={t('forms_builder_logic_delete_rule')}
          onClick={() => onRequestDelete(field.id, rule.id)}
        />
      </div>

      {!collapsed && (
        <div className='form-logic__rule-body'>
          <span className='form-logic__heading'>{t('forms_builder_logic_if_answer')}</span>
          <Dropdown
            open={answerOpen}
            onClose={() => setAnswerOpen(false)}
            align='start'
            width={260}
            portal
            trigger={
              <button
                type='button'
                className='form-logic__answer'
                aria-expanded={answerOpen}
                onClick={() => setAnswerOpen((o) => !o)}
              >
                {picked ? (
                  <span className='form-logic__answer-chip' style={chipStyle(picked.itemColor)}>
                    {picked.responseText || '—'}
                  </span>
                ) : (
                  <span className='form-logic__answer-ph'>
                    {t('forms_builder_logic_answer_ph')}
                  </span>
                )}
                <Icon name={answerOpen ? 'chevron-up' : 'chevron-down'} size={15} />
              </button>
            }
          >
            {pickableOptions.map((o, i) => (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                type='button'
                className='app-dropdown__item'
                role='menuitem'
                onClick={() => {
                  onSetRuleAnswer(field.id, rule.id, o.value);
                  setAnswerOpen(false);
                }}
              >
                <span className='form-logic__answer-chip' style={chipStyle(o.itemColor)}>
                  {o.responseText || '—'}
                </span>
              </button>
            ))}
          </Dropdown>

          {rule.matchingValue !== '' && (
            <>
              <span className='form-logic__heading'>{t('forms_builder_logic_then')}</span>
              <div className='form-logic__tiles'>
                {ACTION_TILES.map((tile) => (
                  <button
                    key={tile.kind}
                    type='button'
                    className={[
                      'form-logic__tile',
                      action?.kind === tile.kind && 'form-logic__tile--active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={action?.kind === tile.kind}
                    onClick={() =>
                      onSetRuleAction(
                        field.id,
                        rule.id,
                        tile.kind === 'ask_a_question'
                          ? { kind: 'ask_a_question', childType: answerType }
                          : tile.kind === 'user_page'
                            ? { kind: 'user_page', pageId: currentTarget ?? '' }
                            : { kind: tile.kind },
                      )
                    }
                  >
                    <span className='form-logic__tile-icon'>
                      <Icon name={tile.icon} size={14} />
                    </span>
                    {t(tile.labelKey)}
                  </button>
                ))}
              </div>

              {action?.kind === 'ask_a_question' && (
                <div className='form-logic__sub'>
                  <span className='form-logic__heading'>
                    {t('forms_builder_logic_answer_type')}
                  </span>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('forms_builder_logic_answer_type')}
                    value={answerType}
                    options={ANSWER_TYPES.map((type) => {
                      const entry = LIBRARY_BY_TYPE.get(type);
                      return {
                        value: type,
                        label: entry ? t(entry.labelKey) : type,
                        icon: entry?.icon,
                      };
                    })}
                    onChange={(next) =>
                      onSetRuleAction(field.id, rule.id, {
                        kind: 'ask_a_question',
                        childType: next,
                      })
                    }
                  />

                  {/* A checkList follow-up expands inline: its choices, then its
                   * own rules (depth 2 = the cap — no logic block below it). */}
                  {child?.elementType === 'checkList' && (
                    <div className='form-logic__nested'>
                      <div className='form-logic__nested-head'>
                        <span className='form-logic__heading'>
                          {t('forms_builder_logic_its_choices')}
                        </span>
                        <span className='form-logic__level-badge'>
                          {t('forms_builder_logic_level_badge', { n: depth })}
                        </span>
                      </div>
                      <ChoiceOptionsEditor
                        options={optionsOf(child)}
                        onChange={(optionList) => setChildOptions(child, optionList)}
                        onApplyResponseSet={(opts) => onApplyResponseSet(child.id, opts)}
                      />

                      {depth === 1 && (
                        <>
                          <span className='form-logic__heading'>
                            {t('forms_builder_logic_nested_title')}
                          </span>
                          {child.rules.map((nestedRule, k) => (
                            <FormLogicRuleCard
                              key={nestedRule.id}
                              field={child}
                              rule={nestedRule}
                              index={k + 1}
                              depth={2}
                              fields={fields}
                              pages={pages}
                              activePageId={activePageId}
                              collapsedIds={collapsedIds}
                              onToggleCollapse={onToggleCollapse}
                              onAddRule={onAddRule}
                              onSetRuleAnswer={onSetRuleAnswer}
                              onSetRuleAction={onSetRuleAction}
                              onRequestDelete={onRequestDelete}
                              onUpdateFieldProperties={onUpdateFieldProperties}
                              onApplyResponseSet={onApplyResponseSet}
                            />
                          ))}
                          <button
                            type='button'
                            className='form-logic__add form-logic__add--nested'
                            disabled={!hasFreeRuleValue(child)}
                            title={
                              !hasFreeRuleValue(child)
                                ? t('forms_builder_logic_add_rule_disabled')
                                : undefined
                            }
                            onClick={() => onAddRule(child.id)}
                          >
                            <Icon name='plus' size={15} />
                            {t('forms_builder_logic_add_rule')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {action?.kind === 'user_page' && (
                <div className='form-logic__sub'>
                  <span className='form-logic__heading'>{t('forms_builder_logic_goto_page')}</span>
                  {pageOptions.length ? (
                    <SelectDropdown
                      size='sm'
                      aria-label={t('forms_builder_logic_goto_page')}
                      value={currentTarget}
                      placeholder={t('forms_builder_logic_goto_ph')}
                      options={pageOptions}
                      onChange={(next) =>
                        onSetRuleAction(field.id, rule.id, { kind: 'user_page', pageId: next })
                      }
                    />
                  ) : (
                    <span className='form-logic__sub-empty'>
                      {t('forms_builder_logic_goto_none')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default FormLogicRuleCard;
